import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Switch, Paper, CircularProgress,
  Divider, Snackbar, Alert, Grid, Avatar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../services/firestoreService';
import { storage } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const emptyForm = () => ({
  id: '', name: '', address: '', phone: '', email: '', website: '', logoURL: '',
});

const ManageBranches = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [logoFile, setLogoFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCollection('branches');
      setItems(data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      console.error('Error loading branches:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && f.type.match(/image.*/)) setLogoFile(f);
  };

  const uploadLogo = async (branchId) => {
    if (!logoFile) return form.logoURL || '';
    setUploading(true);
    try {
      const ext = logoFile.name.split('.').pop().toLowerCase();
      const path = `branches/${branchId}/logo.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytesResumable(storageRef, logoFile);
      const url = await getDownloadURL(storageRef);
      setUploading(false);
      return url;
    } catch (error) {
      setUploading(false);
      console.error('Error uploading logo:', error);
      return form.logoURL || '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      website: form.website.trim(),
      active: true,
    };
    try {
      let branchId = form.id;
      if (form.id) {
        payload.logoURL = await uploadLogo(form.id);
        await updateDocument('branches', form.id, payload);
      } else {
        branchId = await addDocument('branches', payload);
        const logoURL = await uploadLogo(branchId);
        if (logoURL) await updateDocument('branches', branchId, { logoURL });
      }
      setForm(emptyForm());
      setLogoFile(null);
      setSnackbar({ open: true, message: form.id ? 'Sucursal actualizada' : 'Sucursal agregada', severity: 'success' });
      load();
    } catch (error) {
      console.error('Error saving branch:', error);
      setSnackbar({ open: true, message: error.message || 'Error al guardar sucursal', severity: 'error' });
    }
  };

  const handleToggle = async (item) => {
    await updateDocument('branches', item.id, { active: !item.active });
    load();
  };

  const handleDelete = async (id) => {
    await deleteDocument('branches', id);
    setSnackbar({ open: true, message: 'Sucursal eliminada', severity: 'info' });
    load();
  };

  const startEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      address: item.address || '',
      phone: item.phone || '',
      email: item.email || '',
      website: item.website || '',
      logoURL: item.logoURL || '',
    });
    setLogoFile(null);
  };

  const cancelEdit = () => {
    setForm(emptyForm());
    setLogoFile(null);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Sucursales</Typography>
          <Typography variant="body2" color="text.secondary">Ubicaciones físicas de la organización</Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}><TextField label="Nombre *" fullWidth size="small" value={form.name} onChange={set('name')} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><TextField label="Dirección" fullWidth size="small" value={form.address} onChange={set('address')} /></Grid>
          <Grid size={{ xs: 12, md: 4 }}><TextField label="Teléfono" fullWidth size="small" value={form.phone} onChange={set('phone')} /></Grid>
          <Grid size={{ xs: 12, md: 4 }}><TextField label="Email" fullWidth size="small" value={form.email} onChange={set('email')} /></Grid>
          <Grid size={{ xs: 12, md: 4 }}><TextField label="Sitio web" fullWidth size="small" value={form.website} onChange={set('website')} /></Grid>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} size="small" sx={{ color: '#1A3A5C', borderColor: '#CBD5E1' }}>
                {logoFile ? logoFile.name : form.logoURL ? 'Cambiar logo' : 'Subir logo'}
                <input type="file" accept="image/png, image/jpeg" hidden ref={fileRef} onChange={handleFileSelect} />
              </Button>
              {form.logoURL && <Avatar src={form.logoURL} alt="logo" variant="rounded" sx={{ width: 40, height: 40, objectFit: 'contain' }} />}
              {logoFile && !form.logoURL && <Typography variant="caption" color="text.secondary">{logoFile.name}</Typography>}
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleSave} disabled={!form.name.trim() || uploading}>
            {form.id ? 'Guardar cambios' : 'Agregar sucursal'}
          </Button>
          {form.id && <Button variant="outlined" onClick={cancelEdit} size="small">Cancelar</Button>}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay sucursales configuradas</Typography>
          </Box>
        ) : (
          <List dense>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Switch size="small" checked={!!item.active} onChange={() => handleToggle(item)} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={item.name}
                    secondary={[
                      item.address, item.phone, item.email
                    ].filter(Boolean).join(' · ') || (item.active ? 'Activa' : 'Inactiva')}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => startEdit(item)} sx={{ mr: 1, color: '#1A3A5C' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton edge="end" color="error" size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageBranches;
